import Vue from "vue";
import { cloneDeep } from "lodash";
import TagsCompletionStatus from "@/domain/core/constant/tagsCompletionStatus";
import Downloader from "@/domain/core/utils/downloader";
import { applyPattern } from "@/domain/core/utils/pattern";

/**
 * Creates a fresh context object for media navigation
 */
const createEmptyContext = () => ({
    storage: {},         // Media by ID (dictionary for deduplication)
    sorted: [],          // Sorted media array (computed from storage)
    total: 0,            // Total count from API
    position: null,      // Current photo position (0-indexed)
    photoId: null,       // Photo ID for which position was calculated
    hasBefore: true,     // Can load more before (only fetchBeforeMedia sets to false)
    hasAfter: true,      // Can load more after (only fetchAfterMedia sets to false)
    filters: {}          // Filters used for this context
})

/**
 * Sort media by ID descending (default sorting: -u means newest first, higher ID = newer)
 */
const sortMediaById = (a, b) => b.id - a.id

/**
 * Update sorted array from storage (called after any storage modification)
 */
const updateContextSorted = (ctx) => {
    ctx.sorted = Object.values(ctx.storage).sort(sortMediaById)
}

const state = {
    pica_service_id: null,
    gallery_id: null,
    currentPhotoId: null,
    currentPhoto: null,
    currentPhotoMetadata: null,
    loadingMetadata: false,

    // Context system - each context is independent
    contexts: {
        browse: createEmptyContext(),         // Main browse (gallery filters)
        clusterFiltered: createEmptyContext(), // Cluster + browse filters
        clusterAll: createEmptyContext()       // Cluster only (all photos of person)
    },
    activeContextKey: 'browse', // 'browse' | 'clusterFiltered' | 'clusterAll'

    // Cluster info (when viewing cluster photos)
    activeClusterId: null,
    activeClusterThumbUrl: null,
    activeClusterName: null,
    originPhotoId: null, // Photo we started from (for back button)

    // Face selection (resets on photo change)
    selectedFaceId: null,
    highlightedFaceId: null,
    selectedFaceClusterCount: null, // Preview count for selected face (not the active cluster)

    // Tag system
    tagDefinitions: [],
    clusterHighlightPattern: '',
    clusterNamePattern: '',

    // Loading states
    loading: false,
    loadingPhoto: false,

    // Permissions
    permissions: {
        canEdit: false,
        canDelete: false,
        canDeleteCluster: false,
        canUpdateFaceTags: false,
        canUpdateClusterTags: false,
        canSeeFaces: false,
        canLinkPicaCode: false,
        canUnlinkPicaCode: false
    }
}

/**
 * Calculate tags completion status for a face
 * face.tags contains cluster tags directly (flat, no wrapper)
 */
const calculateTagsCompletionStatus = (face, tagDefinitions) => {
    const requiredClusterTagDefs = tagDefinitions.filter(
        def => def.scopes.includes('cluster') && def.is_required
    )

    const totalRequired = requiredClusterTagDefs.length
    if (totalRequired === 0) return TagsCompletionStatus.COMPLETE

    let filledRequiredCount = 0
    if (face.cluster_id) {
        const clusterTags = face.tags || {}
        filledRequiredCount = requiredClusterTagDefs.filter(def => {
            const value = clusterTags[def.code]
            return value !== null && value !== undefined && value !== ''
        }).length
    }

    if (filledRequiredCount === 0) return TagsCompletionStatus.EMPTY
    if (filledRequiredCount < totalRequired) return TagsCompletionStatus.PARTIAL
    return TagsCompletionStatus.COMPLETE
}

/**
 * Process faces with tags completion status
 */
const processFaces = (faces, tagDefinitions) => {
    if (!faces || !Array.isArray(faces)) return []
    return faces.map(face => {
        face.tagsCompletionStatus = calculateTagsCompletionStatus(face, tagDefinitions)
        return face
    })
}

export const getters = {
    // Base getters
    pica_service_id: state => state.pica_service_id,
    gallery_id: state => state.gallery_id,
    route_param: state => ({
        pica_service_id: state.pica_service_id,
        gallery_id: state.gallery_id
    }),

    // Current photo
    currentPhotoId: state => state.currentPhotoId,
    currentPhoto: state => state.currentPhoto,
    currentPhotoMetadata: state => state.currentPhotoMetadata,
    loadingMetadata: state => state.loadingMetadata,

    // Active context and its data
    activeContextKey: state => state.activeContextKey,
    activeContext: state => state.contexts[state.activeContextKey],
    contexts: state => state.contexts,

    // Getters that delegate to active context
    photoList: (state, getters) => getters.activeContext.sorted,
    totalPhotos: (state, getters) => getters.activeContext.total,
    currentPosition: (state, getters) => {
        const ctx = getters.activeContext
        const idx = ctx.sorted.findIndex(p => p.id === state.currentPhotoId)
        if (idx >= 0) {
            // If we have a server position and the photo matches, use it as base
            if (ctx.photoId === state.currentPhotoId && ctx.position !== null) {
                return ctx.position + 1 // 1-indexed for display
            }
            // Fallback: estimate position based on local index
            return idx + 1
        }
        return null
    },
    hasBefore: (state, getters) => getters.activeContext.hasBefore,
    hasAfter: (state, getters) => getters.activeContext.hasAfter,

    // Navigation
    hasPrev: (state, getters) => {
        const ctx = getters.activeContext
        const idx = ctx.sorted.findIndex(p => p.id === state.currentPhotoId)
        return idx > 0
    },
    hasNext: (state, getters) => {
        const ctx = getters.activeContext
        const idx = ctx.sorted.findIndex(p => p.id === state.currentPhotoId)
        return idx >= 0 && idx < ctx.sorted.length - 1
    },

    // Face selection
    selectedFaceId: state => state.selectedFaceId,
    selectedFace: state => {
        if (!state.currentPhoto?.faces || state.selectedFaceId === null) return null
        return state.currentPhoto.faces.find(face => face.id === state.selectedFaceId)
    },
    selectedFaceClusterCount: state => state.selectedFaceClusterCount,
    selectedClusterId: state => {
        const face = state.currentPhoto?.faces?.find(f => f.id === state.selectedFaceId)
        return face?.cluster_id || null
    },
    selectedCluster: state => {
        const face = state.currentPhoto?.faces?.find(f => f.id === state.selectedFaceId)
        if (!face || !face.cluster_id) return null
        return {
            id: face.cluster_id,
            tags: face.tags || {},
            picacode: face.picacode
        }
    },
    highlightedFaceId: state => state.highlightedFaceId,

    // Tag system
    tagDefinitions: state => state.tagDefinitions,
    tagDefinitionsByScope: state => scope => {
        return state.tagDefinitions.filter(def => def.scopes.includes(scope))
    },
    clusterHighlightPattern: state => state.clusterHighlightPattern,
    clusterNamePattern: state => state.clusterNamePattern,

    // Loading states
    loading: state => state.loading,
    loadingPhoto: state => state.loadingPhoto,

    // Permissions
    permissions: (state, getters, rootState, rootGetters) => {
        const photoSearchPermissions = rootGetters['photoSearch/permissions'] || {}
        return {
            ...state.permissions,
            canDelete: photoSearchPermissions.canDeleteFace || state.permissions.canDelete,
            canDeleteCluster: photoSearchPermissions.canDeleteFace || state.permissions.canDeleteCluster,
            canLinkPicaCode: photoSearchPermissions.canSetPicaCode || state.permissions.canLinkPicaCode,
            canUnlinkPicaCode: photoSearchPermissions.canUnlinkPicaCode || state.permissions.canUnlinkPicaCode,
            canUpdateFaceTags: photoSearchPermissions.canUpdateFaceTags || state.permissions.canUpdateFaceTags,
            canUpdateClusterTags: photoSearchPermissions.canUpdateFaceTags || state.permissions.canUpdateClusterTags
        }
    },

    // Cluster filter state
    activeClusterId: state => state.activeClusterId,
    activeClusterName: state => state.activeClusterName,
    activeClusterThumbUrl: state => state.activeClusterThumbUrl,

    // Tab system
    // Show tabs only if cluster filter active AND counts are different
    showClusterTabs: state => {
        if (!state.activeClusterId) return false
        const filteredCount = state.contexts.clusterFiltered.total
        const allCount = state.contexts.clusterAll.total
        return filteredCount !== allCount
    },
    canGoBack: state => state.originPhotoId !== null,

    // Counts from contexts
    browseCount: state => state.contexts.browse.total,
    clusterFilteredCount: state => state.contexts.clusterFiltered.total,
    clusterAllCount: state => state.contexts.clusterAll.total,

    // Active cluster object (built from face data)
    activeCluster: state => {
        if (!state.activeClusterId) return null
        const face = state.currentPhoto?.faces?.find(f => f.cluster_id === state.activeClusterId)
        if (face) {
            return {
                id: face.cluster_id,
                tags: face.tags || {},
                picacode: face.picacode
            }
        }
        return { id: state.activeClusterId, tags: {}, picacode: null }
    }
}

/**
 * Recalculate tags completion status for all faces in current photo
 */
const recalculateFacesCompletionStatus = (state) => {
    if (!state.currentPhoto?.faces) return
    state.currentPhoto.faces = state.currentPhoto.faces.map(face => {
        face.tagsCompletionStatus = calculateTagsCompletionStatus(face, state.tagDefinitions)
        return face
    })
}

export const mutations = {
    // Reset entire store
    reset(state) {
        state.pica_service_id = null
        state.gallery_id = null
        state.currentPhotoId = null
        state.currentPhoto = null
        state.currentPhotoMetadata = null
        state.loadingMetadata = false

        // Reset contexts
        state.contexts = {
            browse: createEmptyContext(),
            clusterFiltered: createEmptyContext(),
            clusterAll: createEmptyContext()
        }
        state.activeContextKey = 'browse'

        // Reset cluster state
        state.activeClusterId = null
        state.activeClusterThumbUrl = null
        state.activeClusterName = null
        state.originPhotoId = null

        // Reset face selection
        state.selectedFaceId = null
        state.highlightedFaceId = null
        state.selectedFaceClusterCount = null

        // Reset loading states
        state.loading = false
        state.loadingPhoto = false
    },

    // Base mutations
    setPicaServiceId(state, pica_service_id) {
        state.pica_service_id = pica_service_id
    },
    setGalleryId(state, gallery_id) {
        state.gallery_id = gallery_id > 0 ? gallery_id : null
    },
    setCurrentPhotoId(state, photo_id) {
        state.currentPhotoId = photo_id
    },
    setCurrentPhoto(state, photo) {
        state.currentPhoto = photo
    },
    setCurrentPhotoMetadata(state, metadata) {
        state.currentPhotoMetadata = metadata
    },
    setLoadingMetadata(state, loading) {
        state.loadingMetadata = loading
    },

    // Context mutations
    setActiveContextKey(state, key) {
        state.activeContextKey = key
    },
    resetContext(state, contextKey) {
        Vue.set(state.contexts, contextKey, createEmptyContext())
    },
    addContextMedia(state, { contextKey, media }) {
        const ctx = state.contexts[contextKey]
        media.forEach(photo => {
            if (!ctx.storage[photo.id]) {
                Vue.set(ctx.storage, photo.id, photo)
            }
        })
        updateContextSorted(ctx)
    },
    setContextStats(state, { contextKey, total, position, photoId }) {
        const ctx = state.contexts[contextKey]
        if (total !== undefined) ctx.total = total
        if (position !== undefined) ctx.position = position
        if (photoId !== undefined) ctx.photoId = photoId
    },
    setContextPagination(state, { contextKey, hasBefore, hasAfter }) {
        const ctx = state.contexts[contextKey]
        if (hasBefore !== undefined) ctx.hasBefore = hasBefore
        if (hasAfter !== undefined) ctx.hasAfter = hasAfter
    },
    setContextFilters(state, { contextKey, filters }) {
        state.contexts[contextKey].filters = filters || {}
    },
    adjustContextPosition(state, { contextKey, delta, newPhotoId }) {
        const ctx = state.contexts[contextKey]
        if (ctx.position !== null) {
            ctx.position += delta
        }
        // Update photoId atomically with position to keep them in sync
        if (newPhotoId !== undefined) {
            ctx.photoId = newPhotoId
        }
    },
    setContextPhotoId(state, { contextKey, photoId }) {
        const ctx = state.contexts[contextKey]
        // If photoId changes without going through adjustContextPosition,
        // invalidate position since we don't know the server position for this photo
        if (ctx.photoId !== photoId) {
            ctx.position = null
        }
        ctx.photoId = photoId
    },

    // Cluster state
    setActiveClusterId(state, clusterId) {
        state.activeClusterId = clusterId
    },
    setActiveClusterThumbUrl(state, url) {
        state.activeClusterThumbUrl = url
    },
    setActiveClusterName(state, name) {
        state.activeClusterName = name
    },
    setOriginPhotoId(state, photoId) {
        state.originPhotoId = photoId
    },

    // Face selection
    setSelectedFaceId(state, face_id) {
        state.selectedFaceId = face_id
        state.selectedFaceClusterCount = null // Reset count when face changes
    },
    setSelectedFaceClusterCount(state, count) {
        state.selectedFaceClusterCount = count
    },
    setHighlightedFaceId(state, face_id) {
        state.highlightedFaceId = face_id
    },

    // Tag system
    setTagDefinitions(state, definitions) {
        state.tagDefinitions = definitions
    },
    setClusterHighlightPattern(state, pattern) {
        state.clusterHighlightPattern = pattern || ''
    },
    setClusterNamePattern(state, pattern) {
        state.clusterNamePattern = pattern || ''
    },

    // Loading states
    setLoading(state, loading) {
        state.loading = loading
    },
    setLoadingPhoto(state, loading) {
        state.loadingPhoto = loading
    },

    // Permissions
    setPermissions(state, permissions) {
        state.permissions = { ...state.permissions, ...permissions }
    },

    // Tag updates
    updateFaceTags(state, { face_id, tags }) {
        if (!state.currentPhoto?.faces) return
        const face = state.currentPhoto.faces.find(f => f.id === face_id)
        if (face) {
            Vue.set(face, 'tags', tags)
            recalculateFacesCompletionStatus(state)
        }
    },
    updateMediaTags(state, tags) {
        if (!state.currentPhoto) return
        Vue.set(state.currentPhoto, 'tags', { media: tags })
    },
    updateClusterTags(state, { cluster_id, tags }) {
        if (!state.currentPhoto?.faces) return
        state.currentPhoto.faces.forEach(face => {
            if (face.cluster_id === cluster_id) {
                Vue.set(face, 'tags', tags)
            }
        })
        recalculateFacesCompletionStatus(state)
    },
    deleteFace(state, face_id) {
        if (!state.currentPhoto?.faces) return
        const index = state.currentPhoto.faces.findIndex(f => f.id === face_id)
        if (index !== -1) {
            state.currentPhoto.faces.splice(index, 1)
        }
    },

    // Tag suggestions
    updateTagSuggestions(state, { scope, tags }) {
        if (!tags || !state.tagDefinitions) return
        state.tagDefinitions.forEach(def => {
            if (def.input_type !== 'tags' || !def.scopes.includes(scope)) return
            const tagValue = tags[def.code]
            if (!tagValue) return
            const values = Array.isArray(tagValue) ? tagValue : [tagValue]
            values.forEach(value => {
                if (!value || typeof value !== 'string') return
                if (!def.suggestions) Vue.set(def, 'suggestions', [])
                const existing = def.suggestions.find(s => s.value === value)
                if (existing) {
                    existing.count += 1
                } else {
                    def.suggestions.push({ value, count: 1 })
                }
            })
        })
    }
}

export const actions = service => ({
    /**
     * Reset the entire store state
     */
    reset({ commit }) {
        commit('reset')
    },

    /**
     * Initialize photoArchive with a specific photo
     */
    async init({ commit, dispatch, state, rootGetters }, { picaServiceId, galleryId, photoId }) {
        commit('setPicaServiceId', picaServiceId)
        commit('setGalleryId', galleryId)
        commit('setCurrentPhotoId', photoId)
        commit('setLoading', true)

        // Reset all contexts
        commit('resetContext', 'browse')
        commit('resetContext', 'clusterFiltered')
        commit('resetContext', 'clusterAll')
        commit('setActiveContextKey', 'browse')

        // Reset cluster state
        commit('setActiveClusterId', null)
        commit('setActiveClusterThumbUrl', null)
        commit('setActiveClusterName', null)
        commit('setOriginPhotoId', null)

        // Get filters from photoSearch store
        const photoSearchFilters = rootGetters['photoSearch/filters'] || {}

        try {
            // Fetch config and tag definitions first (needed for processing)
            await Promise.all([
                dispatch('fetchConfig'),
                dispatch('fetchTagDefinitions')
            ])

            // Load browse context with photoSearch filters
            await dispatch('initContext', {
                contextKey: 'browse',
                filters: photoSearchFilters,
                photoId
            })

            // Fetch photos before the current one (for backward navigation)
            const ctx = state.contexts.browse
            if (ctx.hasBefore) {
                await dispatch('fetchBeforeMedia', 'browse')
            }

            // Then fetch photo detail and metadata
            await Promise.all([
                dispatch('fetchCurrentPhoto'),
                dispatch('fetchPhotoMetadata')
            ])

            // Auto-select face if there's only one
            const faces = state.currentPhoto?.faces
            if (faces && faces.length === 1) {
                await dispatch('selectFace', faces[0].id)
            }
        } finally {
            commit('setLoading', false)
        }
    },

    /**
     * Initialize or refresh a context with filters
     * If photoId is provided, loads media starting from that photo (included via include_cursor)
     */
    async initContext({ commit, getters }, { contextKey, filters = {}, photoId = null }) {
        commit('resetContext', contextKey)
        commit('setContextFilters', { contextKey, filters })

        const queryFilters = { ...filters }
        // cursor_after_id + include_cursor: fetches photos after AND includes the photo itself
        const fetchOptions = photoId ? { cursor_after_id: photoId, include_cursor: true } : {}

        try {
            // Fetch media first
            const mediaResponse = await service.fetchPhotosForFilter(getters.route_param, queryFilters, fetchOptions)
            const media = Array.isArray(mediaResponse) ? mediaResponse : (mediaResponse?.media || [])
            commit('addContextMedia', { contextKey, media })

            // Try to fetch stats with photoId first, fallback to stats without photoId if 404
            let statsResponse
            try {
                statsResponse = photoId
                    ? await service.fetchMediaStats(getters.route_param, { filters: queryFilters, photo_id: photoId })
                    : await service.fetchMediaStats(getters.route_param, { filters: queryFilters })
            } catch (statsError) {
                // If photo doesn't exist in this context (404), fetch stats without photo_id
                statsResponse = await service.fetchMediaStats(getters.route_param, { filters: queryFilters })
                // Clear photoId since it doesn't exist in this context
                photoId = null
            }

            const total = statsResponse.total || media.length
            const position = photoId ? (statsResponse.position ?? 0) : null
            commit('setContextStats', {
                contextKey,
                total,
                position,
                photoId: photoId
            })

            // Only set hasAfter here - hasBefore stays true until fetchBeforeMedia finds nothing
            const hasAfter = position !== null
                ? (position + media.length) < total
                : media.length < total
            commit('setContextPagination', {
                contextKey,
                hasAfter
            })
        } catch (error) {
            console.error(`[PhotoArchive] Error initializing context ${contextKey}:`, error)
        }
    },

    /**
     * Fetch more media after current list (scroll down)
     */
    async fetchAfterMedia({ commit, getters, state }, contextKey = null) {
        const key = contextKey || state.activeContextKey
        const ctx = state.contexts[key]

        if (!ctx.hasAfter || ctx.sorted.length === 0) return

        // Get last media ID for cursor
        const lastMedia = ctx.sorted[ctx.sorted.length - 1]
        if (!lastMedia) return

        try {
            const response = await service.fetchPhotosForFilter(
                getters.route_param,
                ctx.filters,
                { cursor_after_id: lastMedia.id }
            )

            const media = Array.isArray(response) ? response : (response?.media || [])
            commit('addContextMedia', { contextKey: key, media })

            // Update hasAfter - re-read ctx after mutation for updated sorted length
            const updatedCtx = state.contexts[key]
            commit('setContextPagination', {
                contextKey: key,
                hasAfter: media.length > 0 && updatedCtx.sorted.length < updatedCtx.total
            })
        } catch (error) {
            console.error(`[PhotoArchive] Error fetching after media for ${key}:`, error)
        }
    },

    /**
     * Fetch more media before current list (scroll up)
     * When sorted is empty (initial fetch), uses include_cursor to include the photo at cursorId
     */
    async fetchBeforeMedia({ commit, getters, state }, contextKey = null) {
        const key = contextKey || state.activeContextKey
        const ctx = state.contexts[key]

        if (!ctx.hasBefore) return

        // Get first media ID for cursor, or use photoId if sorted is empty (initial fetch case)
        const isInitialFetch = ctx.sorted.length === 0
        const cursorId = isInitialFetch ? ctx.photoId : ctx.sorted[0].id
        if (!cursorId) return

        // Build fetch options - on initial fetch, include_cursor to get the photo itself
        const fetchOptions = { cursor_before_id: cursorId }
        if (isInitialFetch) {
            fetchOptions.include_cursor = true
        }

        try {
            const response = await service.fetchPhotosForFilter(
                getters.route_param,
                ctx.filters,
                fetchOptions
            )

            const media = Array.isArray(response) ? response : (response?.media || [])
            commit('addContextMedia', { contextKey: key, media })

            // Update hasBefore - if we got no results, we've reached the start
            commit('setContextPagination', {
                contextKey: key,
                hasBefore: media.length > 0
            })
        } catch (error) {
            console.error(`[PhotoArchive] Error fetching before media for ${key}:`, error)
        }
    },

    async fetchConfig({ commit, getters }) {
        try {
            const config = await service.fetchConfig(getters.route_param)
            if (config?.archive_settings) {
                if (config.archive_settings.cluster_highlight_pattern) {
                    commit('setClusterHighlightPattern', config.archive_settings.cluster_highlight_pattern)
                }
                if (config.archive_settings.cluster_name_pattern) {
                    commit('setClusterNamePattern', config.archive_settings.cluster_name_pattern)
                }
            }
        } catch (error) {
            console.error('[PhotoArchive] Error fetching config:', error)
        }
    },

    async fetchTagDefinitions({ commit, getters }) {
        try {
            const definitions = await service.fetchTagDefinitions(getters.route_param)
            commit('setTagDefinitions', definitions)
        } catch (error) {
            console.error('[PhotoArchive] Error fetching tag definitions:', error)
            commit('setTagDefinitions', [])
        }
    },

    async fetchPhotoMetadata({ commit, getters, state }) {
        if (!state.currentPhotoId || state.loadingMetadata) return

        commit('setLoadingMetadata', true)
        try {
            const metadata = await service.fetchPhotoMetadata(getters.route_param, state.currentPhotoId)
            commit('setCurrentPhotoMetadata', metadata)
        } catch (error) {
            console.error('[PhotoArchive] Error fetching photo metadata:', error)
            commit('setCurrentPhotoMetadata', null)
        } finally {
            commit('setLoadingMetadata', false)
        }
    },

    async fetchCurrentPhoto({ commit, getters, state }) {
        const photoId = state.currentPhotoId
        if (!photoId) return

        try {
            const photoDetail = await service.fetchPhotoDetail(getters.route_param, photoId)

            // Find photo in active context for additional data
            const ctx = state.contexts[state.activeContextKey]
            const browsePhoto = ctx.storage[photoId]

            const photo = {
                id: photoId,
                src: browsePhoto?.hd || browsePhoto?.sd || photoDetail?.hd || photoDetail?.sd || photoDetail?.thumb,
                thumb: browsePhoto?.thumb || photoDetail?.thumb,
                fileName: browsePhoto?.file_name || photoDetail?.original_filename,
                original_filename: photoDetail?.original_filename,
                shot_at: photoDetail?.shot_at,
                created_at: photoDetail?.created_at,
                photographer: photoDetail?.photographer || null,
                size: browsePhoto?.size || 'N/A',
                width: photoDetail?.width || null,
                height: photoDetail?.height || null,
                is_processed: browsePhoto?.is_processed ?? true,
                tags: photoDetail?.tags || { media: {} },
                faces: processFaces(photoDetail?.faces || [], state.tagDefinitions)
            }

            commit('setCurrentPhoto', photo)
        } catch (error) {
            console.error('[PhotoArchive] Error fetching current photo:', error)
        }
    },

    /**
     * Change to a different photo
     */
    async changePhoto({ commit, dispatch, state }, photoId) {
        commit('setSelectedFaceId', null)
        commit('setCurrentPhotoId', photoId)
        // Also save in the active context so tab switching remembers this photo
        commit('setContextPhotoId', {
            contextKey: state.activeContextKey,
            photoId
        })
        commit('setCurrentPhotoMetadata', null)
        commit('setLoadingPhoto', true)

        try {
            await Promise.all([
                dispatch('fetchCurrentPhoto'),
                dispatch('fetchPhotoMetadata')
            ])

            // Auto-select face if there's only one
            const faces = state.currentPhoto?.faces
            if (faces && faces.length === 1) {
                await dispatch('selectFace', faces[0].id)
            }
        } finally {
            commit('setLoadingPhoto', false)
        }
    },

    /**
     * Navigate to previous photo
     */
    async navigatePrev({ state, commit, dispatch, getters }) {
        if (!getters.hasPrev) return

        const ctx = getters.activeContext
        const currentIndex = ctx.sorted.findIndex(p => p.id === state.currentPhotoId)
        const targetPhotoId = currentIndex > 0 ? ctx.sorted[currentIndex - 1].id : null
        if (currentIndex > 0 && targetPhotoId) {
            // Update position and photoId atomically BEFORE changePhoto
            commit('adjustContextPosition', {
                contextKey: state.activeContextKey,
                delta: -1,
                newPhotoId: targetPhotoId
            })
            await dispatch('changePhoto', targetPhotoId)
        }
    },

    /**
     * Navigate to next photo
     */
    async navigateNext({ state, commit, dispatch, getters }) {
        if (!getters.hasNext) return

        const ctx = getters.activeContext
        const currentIndex = ctx.sorted.findIndex(p => p.id === state.currentPhotoId)
        const targetPhotoId = currentIndex < ctx.sorted.length - 1 ? ctx.sorted[currentIndex + 1].id : null
        if (currentIndex < ctx.sorted.length - 1 && targetPhotoId) {
            // Update position and photoId atomically BEFORE changePhoto
            commit('adjustContextPosition', {
                contextKey: state.activeContextKey,
                delta: 1,
                newPhotoId: targetPhotoId
            })
            await dispatch('changePhoto', targetPhotoId)
        }
    },

    /**
     * Switch to a different context (tab)
     * Each context remembers its own current photo independently
     */
    async switchContext({ commit, dispatch, state, getters }, contextKey) {
        const previousContextKey = state.activeContextKey
        const currentPhotoId = state.currentPhotoId

        // Save current photo in the context we're leaving
        commit('setContextPhotoId', {
            contextKey: previousContextKey,
            photoId: currentPhotoId
        })

        commit('setActiveContextKey', contextKey)

        let ctx = state.contexts[contextKey]

        // Lazy load media for clusterAll if not yet loaded
        if (contextKey === 'clusterAll' && ctx.sorted.length === 0 && state.activeClusterId) {
            // Use the saved photoId for this context, or fall back to current
            const targetPhotoId = ctx.photoId || currentPhotoId
            await dispatch('initContext', {
                contextKey: 'clusterAll',
                filters: { cluster_id: state.activeClusterId },
                photoId: targetPhotoId
            })
            // Fetch photos before the current one (for backward navigation)
            ctx = state.contexts[contextKey]
            if (ctx.hasBefore) {
                await dispatch('fetchBeforeMedia', 'clusterAll')
            }
            // Refresh ctx reference after fetching
            ctx = state.contexts[contextKey]
        }

        // Determine which photo to show in the new context
        let targetPhotoId = null

        // 1. First try: use the saved photo for this context (if it exists and is loaded)
        if (ctx.photoId && ctx.storage[ctx.photoId]) {
            targetPhotoId = ctx.photoId
        }
        // 2. Fallback: if current photo exists in this context, use it
        else if (ctx.storage[currentPhotoId]) {
            targetPhotoId = currentPhotoId
        }
        // 3. Last fallback: first available photo
        else if (ctx.sorted.length > 0) {
            targetPhotoId = ctx.sorted[0].id
        }

        // Change to the target photo if different from current
        if (targetPhotoId && targetPhotoId !== state.currentPhotoId) {
            await dispatch('changePhoto', targetPhotoId)
        }
    },

    /**
     * View all photos of a cluster (person)
     */
    async viewClusterPhotos({ commit, dispatch, state, getters }) {
        const face = state.currentPhoto?.faces?.find(f => f.id === state.selectedFaceId)
        if (!face?.cluster_id) return

        // Save origin photo for back button
        if (!state.originPhotoId) {
            commit('setOriginPhotoId', state.currentPhotoId)
        }

        // Set cluster info
        commit('setActiveClusterId', face.cluster_id)
        commit('setActiveClusterThumbUrl', face.thumb_url)

        // Compute cluster name from pattern
        const tags = face.tags || {}
        const name = applyPattern(tags, state.clusterNamePattern, null)
        commit('setActiveClusterName', name)

        // Get browse filters for clusterFiltered context
        const browseFilters = state.contexts.browse.filters || {}
        const currentPhotoId = state.currentPhotoId

        // Reset clusterAll context (in case we're switching to a different person)
        commit('resetContext', 'clusterAll')

        // Initialize clusterFiltered with full media, clusterAll only stats (lazy load media later)
        const clusterAllFilters = { cluster_id: face.cluster_id }
        commit('setContextFilters', { contextKey: 'clusterAll', filters: clusterAllFilters })

        await Promise.all([
            dispatch('initContext', {
                contextKey: 'clusterFiltered',
                filters: { ...browseFilters, cluster_id: face.cluster_id },
                photoId: currentPhotoId
            }),
            // Only fetch stats for clusterAll (media loaded on tab switch)
            service.fetchMediaStats(getters.route_param, { filters: clusterAllFilters })
                .then(stats => {
                    commit('setContextStats', {
                        contextKey: 'clusterAll',
                        total: stats.total
                    })
                })
                .catch(() => {})
        ])

        // Fetch photos before the current one (for backward navigation)
        const clusterFilteredCtx = state.contexts.clusterFiltered
        if (clusterFilteredCtx.hasBefore) {
            await dispatch('fetchBeforeMedia', 'clusterFiltered')
        }

        // Switch to clusterFiltered tab
        commit('setActiveContextKey', 'clusterFiltered')

        // If current photo is not in the new context, select the first available photo
        const clusterCtx = state.contexts.clusterFiltered
        if (!clusterCtx.storage[currentPhotoId] && clusterCtx.sorted.length > 0) {
            commit('setCurrentPhotoId', clusterCtx.sorted[0].id)
        }
    },

    /**
     * Go back to origin photo and browse context
     */
    async goBackToOrigin({ commit, state, dispatch }) {
        const originId = state.originPhotoId

        // Reset cluster state
        commit('setActiveClusterId', null)
        commit('setActiveClusterThumbUrl', null)
        commit('setActiveClusterName', null)
        commit('setActiveClusterThumbUrl', null)
        commit('setOriginPhotoId', null)

        // Switch back to browse
        commit('setActiveContextKey', 'browse')

        // Navigate to origin photo if available
        if (originId && originId !== state.currentPhotoId) {
            await dispatch('changePhoto', originId)
        }
    },

    // Face selection
    async selectFace({ commit, dispatch, state, getters }, face_id) {
        if (state.selectedFaceId === face_id) {
            commit('setSelectedFaceId', null)
        } else {
            commit('setSelectedFaceId', face_id)

            // Fetch cluster stats for "Person's photos (X)" link preview
            const face = state.currentPhoto?.faces?.find(f => f.id === face_id)
            if (face?.cluster_id) {
                try {
                    const stats = await service.fetchMediaStats(
                        getters.route_param,
                        { filters: { cluster_id: face.cluster_id } }
                    )
                    commit('setSelectedFaceClusterCount', stats.total)
                } catch (error) {
                    console.error('[PhotoArchive] Error fetching cluster stats:', error)
                }
            }
        }
    },

    highlightFace({ commit }, face_id) {
        commit('setHighlightedFaceId', face_id)
    },

    clearHighlight({ commit }) {
        commit('setHighlightedFaceId', null)
    },

    // Tag updates
    async updateFaceTags({ commit, getters }, { face_id, tags }) {
        await service.updateFaceTags(getters.route_param, face_id, tags)
        commit('updateFaceTags', { face_id, tags })
        commit('updateTagSuggestions', { scope: 'face', tags })
    },

    async updateMediaTags({ commit, getters, state }, tags) {
        await service.updateMediaTags(getters.route_param, state.currentPhotoId, tags)
        commit('updateMediaTags', tags)
        commit('updateTagSuggestions', { scope: 'media', tags })
    },

    async updateClusterTags({ commit, getters }, { cluster_id, tags }) {
        await service.updateClusterTags(getters.route_param, cluster_id, tags)
        commit('updateClusterTags', { cluster_id, tags })
        commit('updateTagSuggestions', { scope: 'cluster', tags })
    },

    // Deletions
    async deleteFace({ commit, getters }, face_id) {
        await service.deleteFace(getters.route_param, face_id)
        commit('deleteFace', face_id)
    },

    async deleteCluster({ getters }, cluster_id) {
        await service.deleteCluster(getters.route_param, cluster_id)
    },

    // Pica code linking
    async linkClusterPicaCode({ getters }, { cluster_id, pica_code }) {
        await service.linkClusterPicaCode(getters.route_param, cluster_id, pica_code)
    },

    async unlinkClusterPicaCode({ getters }, cluster_id) {
        await service.unlinkClusterPicaCode(getters.route_param, cluster_id)
    },

    /**
     * Download all photos from active context.
     * Routes through the unified endpoint, translating the context filter
     * (cluster_id / photographer_id / tags / date_range / pica_code) into
     * the matching MediaBlock fields. Falls back to the legacy flow only
     * if the context carries keys outside the unified contract.
     */
    async downloadContextPhotos({ getters, state, dispatch }) {
        const ctx = state.contexts[state.activeContextKey]
        const filters = cloneDeep(ctx.filters || {})
        const count = ctx.total
        const routeParam = getters.route_param

        const mediaBlock = buildUnifiedMediaBlockFromContext({filters, routeParam})
        if (mediaBlock) {
            return await dispatch('downloader/downloadMedia', {
                name: 'Photo Archive',
                media: mediaBlock,
                options: {},
                count
            }, { root: true })
        }

        return await dispatch('downloader/download', {
            name: 'Photo Archive',
            count,
            onDirect: async () => {
                const response = await service.downloadPhotos(routeParam, filters)
                if (response.files && Array.isArray(response.files)) {
                    await Downloader.multiDownload(response.files)
                } else if (response.status === 'error') {
                    throw new Error(response.message || 'Download failed')
                }
            },
            onBatch: async (page, limit) => {
                const response = await service.downloadPhotos(
                    routeParam, filters, null, false, page, limit
                )
                if (response.files && Array.isArray(response.files)) {
                    await Downloader.multiDownload(response.files)
                    return response.files.length
                }
                return 0
            },
            onEmail: async (email) => {
                await service.downloadPhotos(routeParam, filters, email)
            }
        }, { root: true })
    }
})

const UNIFIED_CONTEXT_FILTER_KEYS = ['tags', 'date_range', 'photographer_id', 'cluster_id', 'pica_code']
const UNIFIED_CONTEXT_KEY_MAP = {pica_code: 'picacode'}

function buildUnifiedMediaBlockFromContext({filters, routeParam}) {
    const keys = Object.keys(filters)
    if (keys.some(key => !UNIFIED_CONTEXT_FILTER_KEYS.includes(key))) {
        return null
    }
    const out = {pica_service_id: routeParam.pica_service_id}
    if (routeParam.gallery_id) out.gallery_id = routeParam.gallery_id
    for (const key of UNIFIED_CONTEXT_FILTER_KEYS) {
        if (filters[key] !== undefined && filters[key] !== null) {
            const apiKey = UNIFIED_CONTEXT_KEY_MAP[key] || key
            out[apiKey] = filters[key]
        }
    }
    return out
}

export default service => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(service)
})
